import { useRef, useState, type ChangeEvent } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { extractErrorMessage } from '../api/errors';
import { bulkCreateParticipants, type BulkParticipantResult } from '../api/tenants';
import { parseParticipantSheet } from '../util/bulkParticipants';
import { useSortedRows } from '../util/tableSort';
import { SortHeader } from './SortHeader';

const STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  created: { label: 'Added', color: 'success' },
  already_member: { label: 'Already a participant', color: 'warning' },
  same_name: { label: 'Already here', color: 'warning' },
  duplicate: { label: 'Duplicate', color: 'default' },
  invalid: { label: 'Not added', color: 'error' },
  license_limit: { label: 'License limit', color: 'error' },
};

const NO_RESULTS: BulkParticipantResult[] = [];
const RESULT_SORT = {
  row: (r: BulkParticipantResult) => r.row,
  name: (r: BulkParticipantResult) => r.full_name || null,
  email: (r: BulkParticipantResult) => r.email ?? null,
  // By the label the chip shows, not the raw backend code.
  result: (r: BulkParticipantResult) => STATUS[r.status]?.label ?? r.status,
};

/**
 * Bulk add for direct-entry tenants: a sheet of people where only the name
 * is required, so participants who will never sign in can be enrolled in
 * one go rather than one at a time.
 */
export function BulkAddParticipantsDialog({
  tenantId,
  open,
  onClose,
  onAdded,
}: {
  tenantId: string;
  open: boolean;
  onClose: () => void;
  /** Called after a batch that added anyone, so the roster reloads. */
  onAdded: () => void;
}) {
  const [text, setText] = useState('');
  const [sendWelcome, setSendWelcome] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    created: number;
    results: BulkParticipantResult[];
  } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Sheet order (row number) by default — the order the operator pasted.
  const { sort, sorted } = useSortedRows(result?.results ?? NO_RESULTS, RESULT_SORT, 'row');
  const sheet = parseParticipantSheet(text);
  const withEmail = sheet.rows.filter((r) => r.email).length;

  const close = () => {
    if (busy) return;
    setText('');
    setSendWelcome(false);
    setError(null);
    setResult(null);
    onClose();
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const content = await file.text();
      setText((prev) => (prev ? `${prev}\n${content}` : content));
    } catch {
      setError('could not read that file');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await bulkCreateParticipants(tenantId, sheet.rows, sendWelcome);
      setResult(res);
      if (res.created > 0) onAdded();
    } catch (e) {
      setError(extractErrorMessage(e, 'could not add the participants'));
    } finally {
      setBusy(false);
    }
  };

  const n = sheet.rows.length;
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Bulk add participants</DialogTitle>
      <DialogContent>
        {!result ? (
          <Stack spacing={2}>
            <DialogContentText>
              Paste or upload a sheet of people. Only the <strong>name</strong> is
              required — leave the email blank for someone who will never sign in.
              Optional columns: email, job title, years of experience. With a header
              row the columns can be in any order; without one they are read as
              name, email, job title, years.
            </DialogContentText>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                Choose file
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv,.txt,text/*"
                onChange={onFile}
                hidden
                data-testid="bulk-add-file-input"
              />
              <Typography variant="body2" color="text.secondary">
                Or paste below.
              </Typography>
            </Stack>
            <TextField
              label="Participants"
              placeholder={'Name,Email,Job title,Years of experience\nPat Lee,,Surgeon,12\nKim Roe,kim@example.org,Fellow,3'}
              fullWidth
              multiline
              minRows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={busy}
              helperText={
                n === 0
                  ? 'No people read yet.'
                  : `${n} ${n === 1 ? 'person' : 'people'}, ${withEmail} with an email` +
                    (sheet.hadHeader ? ' · header row detected' : '')
              }
            />
            {withEmail > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendWelcome}
                    onChange={(e) => setSendWelcome(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Email new participants who have an address a link to set their password"
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Alert severity={result.created === result.results.length ? 'success' : 'info'}>
              Added {result.created} of {result.results.length}.
            </Alert>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortHeader label="Row" col="row" sort={sort} />
                    <SortHeader label="Name" col="name" sort={sort} />
                    <SortHeader label="Email" col="email" sort={sort} />
                    <SortHeader label="Result" col="result" sort={sort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((r) => {
                    const s = STATUS[r.status] ?? { label: r.status, color: 'default' as const };
                    return (
                      <TableRow key={r.row}>
                        <TableCell>{r.row}</TableCell>
                        <TableCell>{r.full_name || '—'}</TableCell>
                        <TableCell>{r.email ?? '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={s.label} color={s.color} />
                          {r.detail && (
                            <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                              {r.detail}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button variant="contained" onClick={() => void submit()} disabled={busy || n === 0}>
            {busy ? 'Adding…' : `Add ${n || ''} ${n === 1 ? 'participant' : 'participants'}`.replace('  ', ' ')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
